import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Center, Flex, Grid, GridItem, Progress, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useStores } from "../../../stores/setup/use-store";
import waterLevelIcon from "../../../assets/event-analysis-card/water-level.svg";
import currentIcon from "../../../assets/event-analysis-card/current.svg";
import arrowIcon from "../../../assets/event-analysis-card/arrow-up.svg";
import Card from "./components/Card";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import { divIcon } from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import Compass from "./components/Compass";
import { IStation } from "../../../stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";
import { IOccurrenceDescription } from "../../../types/IOccurrence";
import { StoreState } from "../../../enum/StoreState";
import { transformGeo } from "../../../util/meteogramCharts/transformGeo/transformGeo";
import { EnvironmentalType } from "../../../enum/EnvironmentalType";
import MapLegend from "./components/MapLegend";
import Meteogram from "./components/Meteogram";

interface WaterFlowTabProps {
  selectedOccurrenceDescription: IOccurrenceDescription | undefined;
  station: IStation | undefined;
}

const WaterFlowTab: React.FC<WaterFlowTabProps> = ({
  selectedOccurrenceDescription,
  station,
}) => {
  const {
    dataStores: { occurrenceStore },
  } = useStores();

  const { t } = useTranslation("translation", {
    keyPrefix: "eventAnalysis",
  });

  const [stationMarkerPosition, setStationMarkerPosition] =
    useState<
      | {
          lat: number;
          lng: number;
        }
      | undefined
    >();

  const statistics = useMemo(() => {
    if (
      occurrenceStore.waterFlowData &&
      Object.keys(occurrenceStore.waterFlowData as object).length
    ) {
      return occurrenceStore.waterFlowData;
    } else {
      return null;
    }
  }, [occurrenceStore.waterFlowData]);

  useEffect(() => {
    if (occurrenceStore.occurrence) {
      const { initialDate, finalDate, stationId, regionId } =
        occurrenceStore.occurrence;

      occurrenceStore.getOccurrenceAnalysis({
        regionId,
        stationId,
        environmentalType: EnvironmentalType.WATER_FLOW,
        initialDate,
        finalDate,
      });
    }

    if (station) {
      setStationMarkerPosition({
        lat: station?.station_lat,
        lng: station?.station_lon,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOccurrenceDescription]);

  return (
    <>
      <Grid templateColumns={"repeat(10, 1fr)"} gap={4}>
        <GridItem colSpan={3}>
          <Flex flexDir="column" gap={4}>
            <Card
              icon={waterLevelIcon}
              title={t("waterLevel")}
              textLeft={t("minimumValue")}
              textRight={t("maxValue")}
              valueLeft={
                statistics
                  ? statistics?.waterLevelData?.min?.toFixed(1) + " " + "m"
                  : ""
              }
              valueRight={
                statistics
                  ? statistics?.waterLevelData?.max.toFixed(1) + " " + "m"
                  : ""
              }
              hideBottomLeftText={true}
              hideBottomRightText={true}
              isLoading={
                selectedOccurrenceDescription &&
                occurrenceStore.state === StoreState.PEDDING
              }
            />

            <Card
              icon={currentIcon}
              title={t("current")}
              textLeft={t("minimumValue")}
              textRight={t("maxValue")}
              valueLeft={
                statistics
                  ? statistics?.currentData?.min.toFixed(1) + " " + t("knots")
                  : ""
              }
              valueRight={
                statistics
                  ? statistics?.currentData?.max.toFixed(1) + " " + t("knots")
                  : ""
              }
              iconLeft={arrowIcon}
              iconLeftRotation={
                statistics ? statistics?.currentDirectionMin + 180 : undefined
              }
              bottomLeftText={
                statistics
                  ? transformGeo(statistics?.currentDirectionMin)
                  : undefined
              }
              iconRight={arrowIcon}
              iconRightRotation={
                statistics ? statistics?.currentDirectionMax + 180 : undefined
              }
              bottomRightText={
                statistics
                  ? transformGeo(statistics?.currentDirectionMax)
                  : undefined
              }
              isLoading={
                selectedOccurrenceDescription &&
                occurrenceStore.state === StoreState.PEDDING
              }
            />
          </Flex>
        </GridItem>
        <GridItem rowSpan={10} colSpan={7}>
          {occurrenceStore.occurrence ? (
            occurrenceStore.waterFlowChart &&
            Object.keys(occurrenceStore.waterFlowChart as object).length ? (
              <>
                <MapLegend
                  averageColor="#9D53F2"
                  averageText={t("minWaterFlowCurrent") as string}
                  maxColor="#286793"
                  maxText={t("maxWaterFlowCurrent") as string}
                />

                <MapContainer
                  key="mapStation"
                  center={stationMarkerPosition}
                  zoom={12}
                  scrollWheelZoom={false}
                  style={{
                    height: "100%",
                    width: "100%",
                    borderRadius: 8,
                  }}
                >
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {stationMarkerPosition && statistics && (
                    <>
                      <Marker
                        position={stationMarkerPosition}
                        icon={divIcon({
                          html: renderToStaticMarkup(
                            <i
                              style={{
                                color: "#000",
                                marginLeft: "-8px",
                                marginTop: "-10px",
                              }}
                              className="fas fa-map-marker-alt fa-2x"
                            />
                          ),
                        })}
                      ></Marker>
                      <Marker
                        position={stationMarkerPosition}
                        icon={divIcon({
                          html: renderToStaticMarkup(
                            <Compass
                              maxDegrees={statistics?.currentDirectionMax?.toString()}
                              maxPointerText={`${statistics?.currentData?.max?.toFixed(
                                0
                              )}kt`}
                              maxReverse
                              averageDegrees={statistics?.currentDirectionMin?.toString()}
                              averagePointerText={`${statistics?.currentData?.min?.toFixed(
                                0
                              )}kt`}
                              averageReverse
                            />
                          ),
                        })}
                      ></Marker>
                    </>
                  )}
                </MapContainer>
              </>
            ) : (
              <Center
                h="100%"
                w="100%"
                borderRadius={8}
                bg="white"
                color="newBlue.500"
              >
                {occurrenceStore.state === StoreState.PEDDING ? (
                  <Progress w="500px" size="xs" isIndeterminate />
                ) : (
                  <Text>{t("noi4castDataFound")}</Text>
                )}
              </Center>
            )
          ) : !selectedOccurrenceDescription ? (
            <Center
              h="100%"
              w="100%"
              borderRadius={8}
              bg="white"
              color="newBlue.500"
            >
              <Text>{t("selectAnOccurrenceToBegin")}</Text>
            </Center>
          ) : (
            <Center
              h="100%"
              w="100%"
              borderRadius={8}
              bg="white"
              color="newBlue.500"
            >
              {occurrenceStore.state === StoreState.PEDDING && (
                <Progress w="500px" size="xs" isIndeterminate />
              )}
            </Center>
          )}
        </GridItem>
      </Grid>

      <Meteogram
        description={selectedOccurrenceDescription}
        chart={occurrenceStore.waterFlowChart}
        timezone={
          occurrenceStore.waterFlowChart?.data?.plotOptions?.series
            .timezoneOffset
        }
        chartXAxis={occurrenceStore.i4castChartXAxis}
        chartYAxis={occurrenceStore.i4castChartYAxis}
      />
    </>
  );
};
export default observer(WaterFlowTab);
